import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Font } from '@customedge/shared';
import { usePopoverPosition, PortaledPopover } from '../ColorPicker';
import { PANEL_FIELD_CLASS, propertyInputStyle } from './PropertyRow';

interface FontOption {
  id: string;
  fontLabel: string;
  fontFile: string;
}

interface FontComboboxProps {
  fonts: Font[];
  value: string;
  onChange: (fontLabel: string) => void;
}

const POPOVER_WIDTH = 252; // matches the panel's own field width (280px panel - 2*12px padding - 4px)
const OPTION_HEIGHT = 30;
const MAX_VISIBLE_OPTIONS = 6;

// Single searchable font control — replaces the old pattern of a standalone
// search `<input>` stacked directly above a plain `<select>` (two separate
// fields for one choice) with one combobox: click the field to open a
// filtered dropdown, type to narrow it live, click a result to pick it. Used
// by both TextTool.tsx (Add) and PropertyPanel.tsx (Edit) so font selection
// looks and behaves identically in both places.
export function FontCombobox({ fonts, value, onChange }: FontComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const systemFonts: FontOption[] = ['Arial', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana', 'Helvetica']
    .map(name => ({ id: name, fontLabel: name, fontFile: '' }));
  const allFonts: FontOption[] = fonts.length > 0 ? fonts : systemFonts;
  const filtered = search.trim()
    ? allFonts.filter(f => f.fontLabel.toLowerCase().includes(search.toLowerCase()))
    : allFonts;

  const popoverHeight = Math.min(filtered.length, MAX_VISIBLE_OPTIONS) * OPTION_HEIGHT + 8 || OPTION_HEIGHT + 8;
  // const { triggerRef, pos } = usePopoverPosition(open, POPOVER_WIDTH, popoverHeight);
  const { triggerRef, pos } = usePopoverPosition(
  open,
  POPOVER_WIDTH,
  popoverHeight
);

const dropdownPos = pos
  ? {
      ...pos,
      top: triggerRef.current
        ? triggerRef.current.getBoundingClientRect().bottom + 4
        : pos.top,
    }
  : null;

  // Reset the filter text (but not the selected value) whenever the popover
  // closes, so reopening always starts from the full list rather than
  // whatever partial search was typed last time.
  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const commit = (fontLabel: string) => {
    onChange(fontLabel);
    setOpen(false);
    setSearch('');
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={triggerRef as React.RefObject<HTMLInputElement>}
        type="text"
        readOnly={!open}
        value={open ? search : value}
        placeholder={t('text.searchFont')}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={e => setSearch(e.target.value)}
        className={PANEL_FIELD_CLASS}
        style={{ ...propertyInputStyle, fontFamily: open ? undefined : value, cursor: open ? 'text' : 'pointer' }}
      />

      {/* {open && pos && ( */}
        {/* // <PortaledPopover top={pos.top} left={pos.left} width={POPOVER_WIDTH} onClose={() => setOpen(false)}> */}
        {open && dropdownPos && (
          <PortaledPopover
    top={dropdownPos.top}
    left={dropdownPos.left}
    width={POPOVER_WIDTH}
    onClose={() => setOpen(false)}
  >
          <div style={{ maxHeight: MAX_VISIBLE_OPTIONS * OPTION_HEIGHT, overflowY: 'auto', margin: '-12px', padding: 4 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--text-muted)' }}>{t('text.noFontsFound')}</div>
            )}
            {filtered.map(f => (
              <button
                key={f.id}
                onClick={() => commit(f.fontLabel)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 8px', border: 'none', borderRadius: 4, cursor: 'pointer',
                  background: f.fontLabel === value ? 'var(--panel-field-bg, #f1f2f4)' : 'transparent',
                  fontFamily: f.fontLabel, fontSize: 13, color: 'var(--text)',
                }}
              >
                {f.fontLabel}
              </button>
            ))}
          </div>
        </PortaledPopover>
      )}
    </div>
  );
}
